import { z } from 'zod';
import { REVENUE_STATUS, PAYOUT_STATUS, PAYOUT_METHOD } from '@artha/types';

// Revenue Schemas
export const RevenueTimeSeriesQuerySchema = z.object({
  groupBy: z.enum(['week', 'month']),
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(12),
});

export const RevenueMetricsResponseSchema = z.object({
  totalRevenue: z.number(),
  totalClicks: z.number(),
  pendingPayouts: z.number(),
  successfulPayouts: z.number(),
  currentMonthRevenue: z.number(),
  lastMonthRevenue: z.number(),
  grossEarnings: z.number(),
  platformFees: z.number(),
  pendingAmount: z.number(),
  dueAmount: z.number(),
  revenueGrowth: z.number(),
  clicksGrowth: z.number().optional(),
  currency: z.string().optional(),
});

export const DashboardMetricsResponseSchema = z.object({
  totalEarnings: z.number(),
  thisWeekEarnings: z.number(),
  thisMonthEarnings: z.number(),
  pendingAmount: z.number(),
  totalClicks: z.number(),
  currency: z.string(),
});

export const RevenueImpactParamsSchema = z.object({
  creatorId: z.string().min(1),
});

export const RevenueImpactResponseSchema = z.object({
  clicks: z.number(),
  applications: z.number(),
  isEstimated: z.boolean(),
  hasMeaningfulActivity: z.boolean(),
  monthName: z.string().optional(),
});

export const LeaderboardQuerySchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  period: z.enum(['week', 'month']).default('week'),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
});

export const LeaderboardEntrySchema = z.object({
  creatorId: z.string(),
  name: z.string(),
  avatarUrl: z.string().optional(),
  earnings: z.number(),
  rank: z.number(),
  isAnonymous: z.boolean(),
});

export const LeaderboardResponseSchema = z.object({
  topCreators: z.array(LeaderboardEntrySchema),
  rankings: z.array(LeaderboardEntrySchema),
  currentUser: z.object({
    rank: z.number(),
    earnings: z.number(),
    isAnonymous: z.boolean(),
  }).optional(),
  pagination: z.object({
    itemCount: z.number(),
    perPage: z.number(),
    currentPage: z.number(),
    pageCount: z.number(),
  }),
});

// Payout Schemas
export const PayoutDetailsSchema = z.object({
  method: z.literal(PAYOUT_METHOD.BANK),
  country: z.string().length(2), // ISO country code
  currency: z.string().length(3), // ISO currency code
  account_type: z.enum(['savings', 'current']).optional(),
  address: z.string().optional(),
  details: z.object({
    beneficiary_name: z.string().min(1),
    bank_account: z.string().min(1),
    bank_name: z.string().optional(),
    ifsc: z.string().optional(),
    routing_number: z.string().optional(),
    swift_code: z.string().optional(),
  }).passthrough(),
});

export const PayoutCreateSchema = z.object({
  payout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  amount: z.number().positive().multipleOf(0.01),
  currency: z.string().length(3).default('USD'),
  payout_method: z.literal(PAYOUT_METHOD.BANK),
  status: z.enum(Object.values(PAYOUT_STATUS) as [string, ...string[]]).default(PAYOUT_STATUS.PENDING),
  reference_id: z.string().optional(),
  note: z.string().optional(),
});

export const PayoutUpdateSchema = z.object({
  payout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format').optional(),
  amount: z.number().positive().multipleOf(0.01).optional(),
  currency: z.string().length(3).optional(),
  payout_method: z.literal(PAYOUT_METHOD.BANK).optional(),
  status: z.enum(Object.values(PAYOUT_STATUS) as [string, ...string[]]).optional(),
  reference_id: z.string().optional(),
  note: z.string().optional(),
}).refine(data => Object.keys(data).length > 0, {
  message: 'At least one field must be provided for update',
});

export const PayoutListQuerySchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(10),
  status: z.enum(Object.values(PAYOUT_STATUS) as [string, ...string[]]).optional(),
  payout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  search: z.string().optional(),
});

export const MarkPayoutPaidSchema = z.object({
  reference_id: z.string().min(1, 'Reference ID is required'),
});

// Import Schemas
export const PayoutImportRowSchema = z.object({
  payout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  amount: z.number().positive().multipleOf(0.01),
  currency: z.string().length(3).default('USD'),
  payout_method: z.literal(PAYOUT_METHOD.BANK),
  status: z.enum(Object.values(PAYOUT_STATUS) as [string, ...string[]]).default(PAYOUT_STATUS.PENDING),
  reference_id: z.string().optional(),
  note: z.string().optional(),
});

export const PayoutManualImportSchema = z.object({
  data: z.array(PayoutImportRowSchema).min(1),
});

export const SyncMilestonesSchema = z.object({
  creatorId: z.string().min(1, 'creatorId is required'),
});

// Type exports
export type RevenueTimeSeriesQuery = z.infer<typeof RevenueTimeSeriesQuerySchema>;
export type RevenueMetricsResponse = z.infer<typeof RevenueMetricsResponseSchema>;
export type DashboardMetricsResponse = z.infer<typeof DashboardMetricsResponseSchema>;
export type PayoutDetails = z.infer<typeof PayoutDetailsSchema>;
export type PayoutCreate = z.infer<typeof PayoutCreateSchema>;
export type PayoutUpdate = z.infer<typeof PayoutUpdateSchema>;
export type PayoutListQuery = z.infer<typeof PayoutListQuerySchema>;
export type MarkPayoutPaid = z.infer<typeof MarkPayoutPaidSchema>;
export type PayoutImportRow = z.infer<typeof PayoutImportRowSchema>;
export type PayoutManualImport = z.infer<typeof PayoutManualImportSchema>;
export type LeaderboardQuery = z.infer<typeof LeaderboardQuerySchema>;
export type LeaderboardResponse = z.infer<typeof LeaderboardResponseSchema>;
export type SyncMilestones = z.infer<typeof SyncMilestonesSchema>;
